import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Link2, Plus, Trash2, Pencil, Eye, EyeOff, ChevronUp, ChevronDown, Loader2, X, Check } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ICON_PRESETS = [
    { label: 'Patreon', icon: 'fa-brands fa-patreon', color: '#FF424D' },
    { label: 'X', icon: 'fa-brands fa-x-twitter', color: '#000000' },
    { label: 'Facebook', icon: 'fa-brands fa-facebook-f', color: '#1877F2' },
    { label: 'Instagram', icon: 'fa-brands fa-instagram', color: '#E4405F' },
    { label: 'YouTube', icon: 'fa-brands fa-youtube', color: '#FF0000' },
    { label: 'TikTok', icon: 'fa-brands fa-tiktok', color: '#000000' },
    { label: 'Ko-fi', icon: 'fa-solid fa-mug-hot', color: '#FF5E5B' },
    { label: 'Buy Me a Coffee', icon: 'fa-solid fa-mug-saucer', color: '#FFDD00' },
    { label: 'PayPal', icon: 'fa-brands fa-paypal', color: '#003087' },
    { label: 'GoFundMe', icon: 'fa-solid fa-hand-holding-heart', color: '#00B964' },
    { label: 'Discord', icon: 'fa-brands fa-discord', color: '#5865F2' },
    { label: 'GitHub', icon: 'fa-brands fa-github', color: '#181717' },
    { label: 'Website', icon: 'fa-solid fa-globe', color: '#2C4C3B' },
    { label: 'Other / Link', icon: 'fa-solid fa-link', color: '#6E7A6E' },
];

const EMPTY = { label: '', handle: '', url: '', icon: 'fa-solid fa-link', color: '#2C4C3B', active: true, order: 0 };

const Preview = ({ link }) => (
    <div className="flex items-center gap-3 bg-brand-card border border-brand-border rounded-2xl p-3 w-full">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm flex-shrink-0" style={{ backgroundColor: link.color }}>
            <i className={link.icon} aria-hidden="true" />
        </span>
        <span className="min-w-0">
            <span className="block text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{link.label || 'Label'}</span>
            <span className="block text-sm text-brand-ink truncate">{link.handle || link.url || 'handle'}</span>
        </span>
    </div>
);

function LinkForm({ initial, onCancel, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const applyPreset = (p) => setForm((f) => ({ ...f, label: f.label || p.label, icon: p.icon, color: p.color }));

    const save = async () => {
        if (!form.label.trim() || !form.url.trim()) { toast.error('Label and URL are required'); return; }
        let url = form.url.trim();
        if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
        setSaving(true);
        try {
            const payload = { ...form, url };
            if (form.id) await api.put(`/admin/social-links/${form.id}`, payload);
            else await api.post('/admin/social-links', payload);
            toast.success('Saved');
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not save');
        } finally { setSaving(false); }
    };

    return (
        <div className="bg-brand-bg2 border border-brand-border rounded-2xl p-4 space-y-3" data-testid="link-form">
            <div className="flex items-center justify-between">
                <h3 className="font-serif text-base text-brand-ink">{form.id ? 'Edit link' : 'New link'}</h3>
                <button onClick={onCancel} data-testid="link-form-cancel" className="text-brand-ink3 hover:text-brand-ink"><X size={16} /></button>
            </div>

            <Preview link={form} />

            <div>
                <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-1.5">Pick an icon</p>
                <div className="flex flex-wrap gap-2">
                    {ICON_PRESETS.map((p) => (
                        <button
                            key={p.label}
                            type="button"
                            onClick={() => applyPreset(p)}
                            title={p.label}
                            data-testid={`preset-${p.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                            className={`w-9 h-9 rounded-xl flex items-center justify-center text-white transition-transform hover:scale-110 ${form.icon === p.icon ? 'ring-2 ring-brand-green ring-offset-1 ring-offset-brand-bg2' : ''}`}
                            style={{ backgroundColor: p.color }}
                        >
                            <i className={p.icon} aria-hidden="true" />
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Label</span>
                    <input data-testid="link-label" value={form.label} onChange={(e) => set('label', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" placeholder="Patreon" />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Handle / subtitle</span>
                    <input data-testid="link-handle" value={form.handle} onChange={(e) => set('handle', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" placeholder="Uniqapps" />
                </label>
            </div>
            <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">URL</span>
                <input data-testid="link-url" value={form.url} onChange={(e) => set('url', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" placeholder="https://patreon.com/Uniqapps" />
            </label>
            <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-brand-ink2">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Color</span>
                    <input type="color" data-testid="link-color" value={form.color} onChange={(e) => set('color', e.target.value)} className="w-9 h-9 rounded-lg border border-brand-border bg-transparent" />
                </label>
                <label className="flex items-center gap-2 text-sm text-brand-ink2 cursor-pointer">
                    <input type="checkbox" data-testid="link-active" checked={form.active} onChange={(e) => set('active', e.target.checked)} className="w-4 h-4 accent-brand-green" />
                    Visible to users
                </label>
            </div>

            <div className="flex gap-2 pt-1">
                <button onClick={save} disabled={saving} data-testid="link-save" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white py-2.5 rounded-full text-sm font-medium disabled:opacity-50">
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save link
                </button>
                <button onClick={onCancel} className="px-4 py-2.5 rounded-full text-sm bg-brand-card border border-brand-border text-brand-ink2">Cancel</button>
            </div>
        </div>
    );
}

export default function AdminLinks() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // link object or 'new'
    const [confirmId, setConfirmId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/social-links');
            setLinks(r.data || []);
        } catch { toast.error('Could not load links'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (user && !user.is_admin) { navigate('/'); return; }
        if (user) load();
    }, [user, load, navigate]);

    if (!user || !user.is_admin) return null;

    const remove = async (l) => {
        if (confirmId !== l.id) { setConfirmId(l.id); setTimeout(() => setConfirmId((c) => (c === l.id ? null : c)), 3000); return; }
        setConfirmId(null);
        try { await api.delete(`/admin/social-links/${l.id}`); toast.success('Deleted'); load(); }
        catch { toast.error('Could not delete'); }
    };

    const toggleActive = async (l) => {
        try { await api.put(`/admin/social-links/${l.id}`, { ...l, active: !l.active }); load(); }
        catch { toast.error('Could not update'); }
    };

    const move = async (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= links.length) return;
        const a = links[idx], b = links[j];
        try {
            await api.put(`/admin/social-links/${a.id}`, { ...a, order: b.order });
            await api.put(`/admin/social-links/${b.id}`, { ...b, order: a.order });
            load();
        } catch { toast.error('Could not reorder'); }
    };

    const onSaved = () => { setEditing(null); load(); };
    const nextOrder = links.length ? Math.max(...links.map((l) => l.order || 0)) + 1 : 0;

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="admin-links">
            <button data-testid="admin-links-back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/profile'))} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <div className="flex items-center justify-between mb-5">
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Admin</p>
                    <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 inline-flex items-center gap-2"><Link2 size={26} /> Support links</h1>
                </div>
                {!editing && (
                    <button data-testid="add-link-btn" onClick={() => setEditing({ ...EMPTY, order: nextOrder })} className="inline-flex items-center gap-1.5 text-sm bg-brand-green hover:bg-brand-greenDark text-white rounded-full px-3.5 py-2 font-medium">
                        <Plus size={15} /> Add
                    </button>
                )}
            </div>

            {editing && (
                <div className="mb-6">
                    <LinkForm initial={editing.id ? editing : { ...EMPTY, order: nextOrder }} onCancel={() => setEditing(null)} onSaved={onSaved} />
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-20 text-brand-ink3"><Loader2 className="animate-spin" /></div>
            ) : links.length === 0 ? (
                <p className="text-sm text-brand-ink3 py-10 text-center">No links yet. Tap “Add” to create one.</p>
            ) : (
                <div className="space-y-2" data-testid="links-list">
                    {links.map((l, i) => (
                        <div key={l.id} data-testid={`link-row-${l.id}`} className={`flex items-center gap-2 bg-brand-card border border-brand-border rounded-2xl p-3 ${l.active ? '' : 'opacity-55'}`}>
                            <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm flex-shrink-0" style={{ backgroundColor: l.color }}>
                                <i className={l.icon} aria-hidden="true" />
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-brand-ink truncate">{l.label}</div>
                                <div className="text-[11px] text-brand-ink3 truncate">{l.url}</div>
                            </div>
                            <div className="flex items-center gap-0.5 flex-shrink-0 text-brand-ink3">
                                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`link-up-${l.id}`} className="p-1.5 hover:text-brand-ink disabled:opacity-30"><ChevronUp size={16} /></button>
                                <button onClick={() => move(i, 1)} disabled={i === links.length - 1} data-testid={`link-down-${l.id}`} className="p-1.5 hover:text-brand-ink disabled:opacity-30"><ChevronDown size={16} /></button>
                                <button onClick={() => toggleActive(l)} data-testid={`link-toggle-${l.id}`} className="p-1.5 hover:text-brand-ink">{l.active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                                <button onClick={() => setEditing(l)} data-testid={`link-edit-${l.id}`} className="p-1.5 hover:text-brand-green"><Pencil size={15} /></button>
                                <button onClick={() => remove(l)} data-testid={`link-delete-${l.id}`} className={`p-1.5 ${confirmId === l.id ? 'text-brand-terracotta animate-pulse' : 'hover:text-brand-terracotta'}`} title={confirmId === l.id ? 'Tap again to delete' : 'Delete'}><Trash2 size={15} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
